// DensityAgendaItem — one row in the 3-day density agenda pane. A
// 32x32 type-tinted kind icon (R4 detail-surface /15-alpha tint + a
// 3-letter glyph), the activity name (bold), a meta line (contract
// ref in mono + the status label), and the status label rendered as
// text so severity is never colour-only (R2). The whole row is a
// <button> that opens the activity drawer. Static literal tint/glyph
// maps — no runtime class composition, so no Tailwind safelist entry.

import type { Activity, ActivityKind } from "../../../types/schedule";

interface DensityAgendaItemProps {
  activity: Activity;
  onOpen: (activity: Activity) => void;
}

const KIND_GLYPH: Record<ActivityKind, string> = {
  insurance: "INS",
  deliverable: "DEL",
  milestone: "MIL",
  meeting: "MTG",
  kpi: "KPI",
  variation: "VAR",
};

// R4: a soft /15-alpha tint behind the 3-letter glyph (a detail
// surface — one swatch in focus, not a dense competing list).
//
// Insurance LIGHT glyph is amber-800 (NOT amber-700 like the other
// kinds): amber-700 on bg-amber-500/15 over the light surface is
// 4.30:1 — below WCAG-AA 4.5:1 for the 10px-bold glyph (10px bold
// counts as normal text, not large). amber-800 (#92400e, already used
// elsewhere in the app) is ≈6.0:1. The map is intentionally
// non-uniform: only amber-light fails AA at -700; teal/fuchsia/sky/
// emerald/brand at -700 all PASS (contrast-checked). Do NOT
// "normalise" insurance back to -700. Dark-mode amber-400 (8.33:1)
// is unchanged.
const KIND_ICON_CLASS: Record<ActivityKind, string> = {
  insurance: "bg-amber-500/15 text-amber-800 dark:text-amber-400",
  deliverable: "bg-sky-500/15 text-sky-700 dark:text-sky-400",
  milestone: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  meeting: "bg-brand-500/15 text-brand-700 dark:text-brand-400",
  kpi: "bg-teal-500/15 text-teal-700 dark:text-teal-400",
  variation: "bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-400",
};

export default function DensityAgendaItem({
  activity,
  onOpen,
}: DensityAgendaItemProps) {
  const { kind, name, contract, statusLabel } = activity;
  return (
    <button
      type="button"
      onClick={() => onOpen(activity)}
      aria-label={`${kind} · ${name} · ${contract.ref} · ${statusLabel}`}
      className="flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors hover:bg-slate-50 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:hover:bg-slate-800/60"
    >
      <span
        aria-hidden="true"
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[10px] font-semibold ${KIND_ICON_CLASS[kind]}`}
      >
        {KIND_GLYPH[kind]}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-body-sm font-semibold text-slate-900 dark:text-slate-100">
          {name}
        </span>
        <span className="block truncate text-caption text-slate-500 dark:text-slate-400">
          <span className="font-mono">{contract.ref}</span>
          {" · "}
          {statusLabel}
        </span>
      </span>
    </button>
  );
}
